import { useSettings } from "@/store/settings";
import {
  ApplyRecommendationsRequest,
  BerRatings,
  GetQuotedRecommendationsRequest,
  GetTypologyByBerRequest,
  GetTypologyByBerResponse,
  GetTypologyByUprnRequest,
  GetTypologyByUprnResponse,
  CreatePdfRequest,
  HouseTypology,
  QuoteRecommendationsRequest,
  QuoteRecommendationsResponse,
  RequestError,
  RequestRecommendationsRequest,
  RequestRecommendationsResponse,
  ResponseError,
  ValidateKeyRequest,
  ValidateMPRNRequest,
} from "@/types";
import {
  APPLY_RECOMMENDATIONS,
  BER_RATING, GET_GROUPED_RECOMMENDATIONS,
  CREATE_PDF,
  GET_QUOTED_RECOMMENDATIONS,
  GET_RECOMMENDATIONS,
  GET_RECOMMENDATIONS_FOR_FILE,
  GET_TYPOLOGIES,
  GET_TYPOLOGIES_BY_BER,
  GET_TYPOLOGIES_BY_UPRN,
  QUOTE_RECOMMENDATIONS,
  VALIDATE_KEY,
  VALIDATE_MPRN,
  WRITE_LOG,
} from "@/types/constants";
import { LoanOption } from "@/types/loan";
import { normalizeReportBerRatings } from "@/helpers/ber-rating";
import axios, { AxiosError } from "axios";

type CreatePdfResponse = {
  base64: string;
  filename: string;
  contentType: string;
};

export class GammaAPIService {
  public static async getLoanOption(url: string): Promise<LoanOption> {
    const result = await axios.get(url);
    return result.data;
  }

  public static async validateKey(validateKeyRequest: ValidateKeyRequest) {
    return this.post(
      useSettings().settings?.apiEndpoint + VALIDATE_KEY,
      validateKeyRequest
    );
  }

  public static async getRecommendations(
    requestRecommendationsRequest: RequestRecommendationsRequest
  ) {
    return this.post(
      useSettings().settings?.apiEndpoint + GET_RECOMMENDATIONS,
      requestRecommendationsRequest,
      {
        "Content-Type": "multipart/form-data",
      },
      undefined,
      true
    );
  }

  public static async applyRecommendations(
    applyRecommendationsRequest: ApplyRecommendationsRequest
  ) {
    const response = await this.post(
      useSettings().settings?.apiEndpoint + APPLY_RECOMMENDATIONS,
      applyRecommendationsRequest
    );
    return response?.beforeRating && response?.afterRating
      ? normalizeReportBerRatings(response)
      : response;
  }

  public static async getBerRatings(
    lat: number,
    lng: number
  ): Promise<BerRatings> {
    const response = await axios.get(
      `${useSettings().settings?.apiEndpoint}${BER_RATING}/${lng}/${lat}`
    );
    if (response.status !== 200) throw new Error(response.data);
    return response.data;
  }

  public static async getHouseTypologies(): Promise<HouseTypology[]> {
    return (
      await axios.get(useSettings().settings?.apiEndpoint + GET_TYPOLOGIES)
    ).data;
  }

  public static async getRecommendationList(): Promise<HouseTypology[]> {
    return (
      await axios.get(useSettings().settings?.apiEndpoint + GET_GROUPED_RECOMMENDATIONS)
    ).data;
  }

  public static async getTypologiesByBER(
    request: GetTypologyByBerRequest
  ): Promise<GetTypologyByBerResponse> {
    try {
      const response = await axios.post(
        useSettings().settings?.apiEndpoint + GET_TYPOLOGIES_BY_BER,
        request
      );
      return response.data;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new Error((e as AxiosError<RequestError>).response?.data.message);
      }

      throw e;
    }
  }

  public static async getTypologiesByUPRN(
    request: GetTypologyByUprnRequest
  ): Promise<GetTypologyByUprnResponse> {
    try {
      const response = await axios.post(
        useSettings().settings?.apiEndpoint + GET_TYPOLOGIES_BY_UPRN,
        request
      );
      return response.data;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        throw new Error((e as AxiosError<RequestError>).response?.data.message);
      }
      throw e;
    }
  }

  public static async validateMPRN(validateMPRNRequest: ValidateMPRNRequest) {
    return this.post(
      useSettings().settings?.apiEndpoint + VALIDATE_MPRN,
      validateMPRNRequest,
      {
        "Content-Type": "application/json;charset=UTF-8",
      },
      undefined,
      true
    );
  }

  public static async quoteRecommendations(
    quoteRecommendationsRequest: QuoteRecommendationsRequest
  ) {
    return this.post(
      useSettings().settings?.apiEndpoint + QUOTE_RECOMMENDATIONS,
      quoteRecommendationsRequest
    );
  }

  public static async getQuotedRecommendations(
    getQuotedRecommendationsRequest: GetQuotedRecommendationsRequest
  ): Promise<QuoteRecommendationsResponse> {
    const response = await this.post(
      useSettings().settings?.apiEndpoint + GET_QUOTED_RECOMMENDATIONS,
      getQuotedRecommendationsRequest
    );
    if (response?.recommendationsReport) {
      normalizeReportBerRatings(response.recommendationsReport);
    }
    return response;
  }

  public static async getRecommendationsForFile(
    file: File
  ): Promise<RequestRecommendationsResponse | ResponseError> {
    return this.post(
      useSettings().settings?.apiEndpoint + GET_RECOMMENDATIONS_FOR_FILE,
      { file },
      { "Content-Type": "multipart/form-data" },
      undefined,
      true
    );
  }

  public static async logEvent(eventName: string, eventData: any) {
    return this.post(
      useSettings().settings?.apiEndpoint + WRITE_LOG + `/${eventName}`,
      eventData
    );
  }

  public static async createPdf(request: CreatePdfRequest): Promise<CreatePdfResponse> {
    const response = await this.post(
      useSettings().settings?.apiEndpoint + CREATE_PDF,
      request
    );

    if (typeof response === "string") {
      return {
        base64: response,
        filename: "home-upgrade-roadmap.pdf",
        contentType: "application/pdf",
      };
    }

    return {
      base64:
        response?.base64 ??
        response?.data ??
        response?.fileContents ??
        response?.content ??
        "",
      filename:
        response?.filename ??
        response?.fileName ??
        "home-upgrade-roadmap.pdf",
      contentType:
        response?.contentType ??
        response?.mimeType ??
        "application/pdf",
    };
  }

  private static async post(
    url: string,
    request: any,
    headers?: any,
    cancelToken?: any,
    getError?: boolean
  ) {
    let error = undefined;
    const result = await axios
      .post(url, request, {
        headers: {
          ...(headers || {}),
        },
        cancelToken: cancelToken,
      })
      .catch(
        ({ response }) =>
          (error = getError
            ? response.data.exceptionType
              ? response.data
              : {
                  ...response.data,
                  exceptionType: response.status,
                  code: response.status,
                }
            : undefined)
      );
    return error ? error : result && result.data ? result.data : undefined;
  }
}
